import { useState } from "react";
import dynamic from "next/dynamic";
import ModalVideo from "react-modal-video";
import "react-modal-video/scss/modal-video.scss";
import Link from "next/link";

import { LayoutOne } from "@/layouts";
import { useSelector } from "react-redux";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import { getProducts, productSlug } from "@/lib/product";
import { isProjectsEnabled } from "@/lib/projects";
import { resolveProductImage } from "@/lib/productImage";
import { buildProjectDetailSchema } from "@/lib/projectDetailSchema";
import { Container, Row, Col } from "react-bootstrap";
import FollowUs from "@/components/followUs";
import SafeImage from "@/components/SafeImage";

// Lazy-load the enquiry modal — only needed on click. Pulls in
// framer-motion + a chunk of SCSS that we don't want on the initial
// project-detail payload.
const SendEnquiryButton = dynamic(
  () => import("@/components/enquiry/SendEnquiryButton"),
  { ssr: false }
);
import OurGroupSection from "@/components/ourGroup";
import { getLatestBlogs } from "@/lib/blog";
import {
  FaArrowRight,
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaChevronDown,
  FaHome,
  FaPlay,
} from "react-icons/fa";

function ProductDetails({ product, latestBlogs = [], relatedProjects = [] }) {
  const reduxProducts = useSelector((state) => state.product.products);
  const allProjects = relatedProjects.length > 0 ? relatedProjects : (reduxProducts || []);
  const latestdBlogs = latestBlogs.length
    ? latestBlogs
    : getProducts([], "buying", "featured", 4);

  const featuredListings = (() => {
    // CMS-normalized projects don't have a legacy "category" field; fall back
    // to a simple "show others as featured" pattern so the sidebar widget
    // always has something to render.
    const others = (allProjects || []).filter((p) => p && p.id !== product.id);
    return others.length > 0 ? others : [product];
  })();

  const [featuredIndex, setFeaturedIndex] = useState(0);
  const featuredItem = featuredListings[featuredIndex] || product;
  // Prefer the API-provided slug (clean, ASCII). Fall back to a slugified
  // title only when the live CMS hasn't returned one (legacy/static data).
  const featuredSlug = featuredItem.slug || productSlug(featuredItem.title);

  const [isOpen, setOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(0);

  // Banner image: prefer the project's actual featured image so each
  // project page shows its own hero (CMS or static). Falls back to a
  // generic product shot if nothing was uploaded.
  const bannerImage = resolveProductImage(
    product.productImg || product.featured_image || "/img/banner/project-banner.webp"
  );

  // FAQs come straight from the CMS (admin AEO blocks). When empty, the
  // FAQ section simply doesn't render.
  const faqs = (Array.isArray(product.faqs) ? product.faqs : []).map((f) => ({
    q: f.question || "",
    a: f.answer || "",
  }));

  // Flatten the CMS amenities into a single list (the admin stores one
  // combined array; the legacy template split them across amenities1/2/3).
  const amenitiesList = [
    ...(Array.isArray(product.amenities1) ? product.amenities1 : []),
  ];

  const galleryAll = product.gallery?.all || [];
  const galleryItems = galleryAll.length
    ? galleryAll
    : [product.gallery?.img1, product.gallery?.img2, product.gallery?.img3].filter(Boolean);

  const videoUrl = product.video?.[0] || "";
  const videoId = extractYouTubeId(videoUrl);

  // Build a Google Maps embed URL keyed off the project's location. No
  // generic fallback — if the CMS has no location, the section is hidden.
  const mapEmbedSrc = product.location
    ? `https://maps.google.com/maps?q=${encodeURIComponent(product.location)}&t=&z=14&ie=UTF8&iwloc=&output=embed`
    : "";

  // Developer / RERA / Possession / Configurations come from the CMS
  // (developer_name, developer_tagline, facts[]). No hardcoded defaults.
  const developerName = product.developer_name || "";
  const developerTagline = product.developer_tagline || "";
  const facts = Array.isArray(product.facts) ? product.facts : [];
  const findFact = (key) => {
    const f = facts.find(
      (row) => String(row.label || "").toLowerCase() === key.toLowerCase()
    );
    return f ? f.value : "";
  };
  const reraValue = findFact("RERA");
  const possessionValue = findFact("Possession");
  const configurationsValue = findFact("Configurations");

  return (
    <>
      <PageViewBeacon
        contentType="project"
        contentId={product.id}
        slug={product.slug}
        path={`/projects/${product.slug}`}
      />
      <SeoHead
        seo={{
          ...product,
          schema_json: buildProjectDetailSchema(product),
        }}
        type="website"
        path={`/projects/${product.slug}`}
        fallbackTitle={product.title}
        fallbackDescription={product.excerpt || product.card_blurb}
        fallbackImage={product.featured_image || product.productImg || ""}
        ogImageOverride={product.featured_image || product.productImg || ""}
        author={product.author}
        authorName={
          product.author && product.author.name ? product.author.name : ""
        }
        publisherName="Siddhivinayak Group"
        extraKeywords={[
          product.title,
          product.heading,
          "Siddhivinayak Group project",
          "2 BHK flats in Nagpur",
          "3 BHK flats in Nagpur",
        ]}
      />
      <LayoutOne
        topbar={true}
        bannerImage={bannerImage}
        bannerTitle={product.heading || product.title}
        showHeading={false}
      >
        {videoId ? (
          <ModalVideo
            channel="youtube"
            autoplay
            isOpen={isOpen}
            videoId={videoId}
            onClose={() => setOpen(false)}
          />
        ) : null}
        {/* <!-- SHOP DETAILS AREA START --> */}
        <div className="ltn__shop-details-area pb-10">
          <Container>
            <Row>
              <Col xs={12} lg={8}>
                <div className="ltn__shop-details-inner ltn__page-details-inner mb-60">
                  <h1>{product.heading || product.title}</h1>
                  <label>
                    <span className="ltn__secondary-color">
                      <i className="flaticon-pin"></i>
                    </span>{" "}
                    {product.location}
                  </label>
                  {(product.content || product.excerpt) ? (
                    <div
                      className="cms-content"
                      dangerouslySetInnerHTML={{
                        __html: product.content || product.excerpt || "",
                      }}
                    />
                  ) : null}

                  {galleryItems.length > 0 && (
                    <>
                      <h2 className="title-2">{product.title} — Project Gallery</h2>
                      <div className="ltn__property-details-gallery mb-30">
                        <div className="row">
                          {galleryItems.map((src, idx) => (
                            <div className="col-md-6" key={idx}>
                              <Link href={src} data-rel="lightcase:myCollection">
                                <SafeImage
                                  className="mb-30"
                                  src={src}
                                  alt={`${product.title} ${idx + 1}`}
                                  width={800}
                                  height={600}
                                  sizes="(max-width: 767px) 100vw, 50vw"
                                  loading="lazy"
                                  style={{ width: "100%", height: "auto" }}
                                />
                              </Link>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {amenitiesList.length > 0 && (
                    <>
                      <h2 className="title-2 mb-10">{product.title} Amenities &amp; Lifestyle Features</h2>
                      <ul className="ltn__amenities-list project-amenities-list mb-60">
                        {amenitiesList.map((single, key) => {
                          // CMS rows are {title, description}; legacy rows
                          // were strings. Render whichever shape we got.
                          const label =
                            (single && typeof single === "object" && single.title) ||
                            (typeof single === "string" ? single : "") ||
                            "";
                          if (!label) return null;
                          return (
                            <li key={key}>
                              <span className="ltn__amenities-icon">
                                <FaCheck />
                              </span>
                              {label}
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  )}

                  {product.content2 ? (
                    <div
                      className="cms-content mb-60"
                      dangerouslySetInnerHTML={{ __html: product.content2 }}
                    />
                  ) : null}

                  {product.location ? (
                    <>
                      <h2 className="title-2">{product.title} — Location &amp; Connectivity</h2>
                      <div className="property-details-google-map mb-60">
                        <iframe
                          src={mapEmbedSrc}
                          width="100%"
                          height="450"
                          style={{ border: 0 }}
                          frameBorder="0"
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Map of ${product.location}`}
                        ></iframe>
                      </div>
                    </>
                  ) : null}

                  {videoUrl && (
                    <>
                      <h2 className="title-2">{product.title} — Walkthrough Video</h2>
                      {videoId ? (
                        <div
                          className="ltn__video-bg-img ltn__video-popup-height-500 bg-overlay-black-50 bg-image mb-60"
                          style={{ backgroundImage: `url("${bannerImage}")` }}
                        >
                          <button
                            type="button"
                            aria-label={`Play ${product.title} walkthrough video`}
                            className="ltn__video-icon-2 ltn__video-icon-2-border---"
                            onClick={() => setOpen(true)}
                          >
                            <FaPlay />
                          </button>
                        </div>
                      ) : (
                        <video
                          className="w-100 mb-60"
                          controls
                          preload="metadata"
                          playsInline
                          poster={bannerImage}
                          aria-label={`${product.title} walkthrough video`}
                        >
                          <source src={videoUrl} type="video/mp4" />
                          Your browser does not support embedded video.
                        </video>
                      )}
                    </>
                  )}

                  {faqs.length > 0 && (
                    <>
                      <h2 className="title-2">Frequently Asked Questions About {product.title}</h2>
                      <div className="ltn__faq-area mb-60">
                        <div className="ltn__faq-list">
                          {faqs.map((item, idx) => (
                            <div
                              key={idx}
                              className={`ltn__faq-item ${activeFaq === idx ? "is-open" : ""}`}
                            >
                              <button
                                type="button"
                                className="ltn__faq-question"
                                onClick={() => setActiveFaq(activeFaq === idx ? -1 : idx)}
                                aria-expanded={activeFaq === idx}
                              >
                                <span>{item.q}</span>
                                <FaChevronDown className="ltn__faq-chevron" />
                              </button>
                              <div className="ltn__faq-answer">
                                <p>{item.a}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </Col>

              <Col xs={12} lg={4}>
                <aside className="sidebar ltn__shop-sidebar ltn__right-sidebar---">
                  <div className="widget ltn__form-widget">
                    <h2 className="ltn__widget-title ltn__widget-title-border-2">
                      Enquire About {product.title}
                    </h2>
                    <p className="mb-3">
                      Send us a quick enquiry and we'll get back to you with more
                      details.
                    </p>
                    <SendEnquiryButton
                      product={product}
                      label="Send Enquiry"
                    />
                  </div>
                  {developerName ? (
                    <div className="widget ltn__menu-widget project-developer-widget">
                      <h2 className="ltn__widget-title ltn__widget-title-border-2">
                        About the Developer — {developerName}
                      </h2>
                      <div className="project-developer-card">
                        <div className="project-developer-card__body">
                          <h3 className="project-developer-card__name">{developerName}</h3>
                          {developerTagline ? (
                            <p className="project-developer-card__tagline">
                              {developerTagline}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {(reraValue || possessionValue || configurationsValue) ? (
                        <ul className="project-developer-stats">
                          {reraValue ? (
                            <li>
                              <span className="project-developer-stats__icon" aria-hidden>
                                <FaCheck />
                              </span>
                              <span className="project-developer-stats__label">RERA</span>
                              <span className="project-developer-stats__value">{reraValue}</span>
                            </li>
                          ) : null}
                          {possessionValue ? (
                            <li>
                              <span className="project-developer-stats__icon" aria-hidden>
                                <FaCalendarAlt />
                              </span>
                              <span className="project-developer-stats__label">Possession</span>
                              <span className="project-developer-stats__value">{possessionValue}</span>
                            </li>
                          ) : null}
                          {configurationsValue ? (
                            <li>
                              <span className="project-developer-stats__icon" aria-hidden>
                                <FaHome />
                              </span>
                              <span className="project-developer-stats__label">Configurations</span>
                              <span className="project-developer-stats__value">{configurationsValue}</span>
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  {featuredListings.length > 0 && (
                    <div className="widget ltn__featured-listing-widget">
                      <h2 className="ltn__widget-title ltn__widget-title-border-2">
                        More Projects by Siddhivinayak Group
                      </h2>
                      <div className="ltn__product-item ltn__product-item-4">
                        <div className="product-img">
                          <Link href={`/projects/${featuredSlug}`}>
                            <SafeImage
                              src={resolveProductImage(
                                featuredItem.productImg || featuredItem.featured_image || ""
                              )}
                              alt={featuredItem.title}
                              width={600}
                              height={400}
                              sizes="(max-width: 991px) 100vw, 33vw"
                              loading="lazy"
                              style={{ width: "100%", height: "auto" }}
                            />
                          </Link>
                          {featuredItem.project_status || featuredItem.status ? (
                            <div className="product-badge">
                              <ul>
                                <li className="sale-badge bg-sale">
                                  {featuredItem.project_status || featuredItem.status}
                                </li>
                              </ul>
                            </div>
                          ) : null}
                        </div>
                        <div className="product-info">
                          <h3 className="product-title">
                            <Link href={`/projects/${featuredSlug}`}>
                              {featuredItem.title}
                            </Link>
                          </h3>
                          {featuredItem.excerpt || featuredItem.location ? (
                            <p className="ltn__product-blurb">
                              {String(featuredItem.excerpt || featuredItem.location)}
                            </p>
                          ) : null}
                        </div>
                        <div className="product-info-bottom">
                          <Link
                            href={`/projects/${featuredSlug}`}
                            className="ltn__product-featured-cta"
                          >
                            View Details <FaArrowRight />
                          </Link>
                        </div>
                      </div>
                      {featuredListings.length > 1 && (
                        <div className="ltn__featured-listing-dots">
                          {featuredListings.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              aria-label={`Show listing ${idx + 1}`}
                              className={`ltn__featured-listing-dot ${idx === featuredIndex ? "is-active" : ""}`}
                              onClick={() => setFeaturedIndex(idx)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="widget ltn__popular-post-widget">
                    <h2 className="ltn__widget-title ltn__widget-title-border-2 latest-blogs-title">
                      Latest Real Estate Insights from Siddhivinayak Group
                    </h2>
                    <ul className="latest-blogs-list">
                      {latestdBlogs.map((blog, key) => {
                        // Prefer the API-provided slug (clean). Fall back to
                        // a slugified title when the live CMS hasn't returned
                        // a slug (e.g. legacy JSON fallback path).
                        const blogSlug = blog.slug || productSlug(blog.title || "");
                        const blogImage =
                          blog.thumbImg ||
                          blog.featured_image ||
                          "/img/blog/default.jpg";
                        return (
                          <li key={blog.id || key} className="latest-blogs-card">
                            <Link
                              href={blogSlug ? `/blog/${blogSlug}` : "/blog"}
                              className="latest-blogs-card__link"
                              aria-label={blog.title || "Blog post"}
                            >
                              <SafeImage
                                src={blogImage}
                                alt={blog.title || "Blog post"}
                                width={120}
                                height={90}
                                sizes="120px"
                                loading="lazy"
                                className="latest-blogs-card__img"
                              />
                              <span
                                className="latest-blogs-card__overlay"
                                aria-hidden="true"
                              />
                              <span className="latest-blogs-card__body">
                                <span className="latest-blogs-card__title">
                                  {blog.title}
                                </span>
                                <span className="latest-blogs-card__date">
                                  <FaCalendarAlt />
                                  {blog.date}
                                </span>
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <FollowUs title="Follow Siddhivinayak Group on Social Media" />
                </aside>
              </Col>
            </Row>
          </Container>
        </div>
        {/* <!-- SHOP DETAILS AREA END --> */}

        <div className="section-bg-1">
          <OurGroupSection />
        </div>
        </LayoutOne>

    </>
  );
}

/** Pull a YouTube video id out of any common URL form, or null. */
function extractYouTubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([\w-]{6,15})/
  );
  return m ? m[1] : null;
}

export default ProductDetails;

export async function getStaticProps({ params }) {
  if (!isProjectsEnabled()) return { notFound: true, revalidate: 60 };
  // Legacy/aliased slug for Hilltop Heights 14 — also resolve under the
  // dynamic route now that the dedicated static page is gone. Hitting
  // the historical CMS-row slug still serves the same project.
  // Legacy/aliased slug for Hilltop Heights 14 — the historical CMS-row slug
  // was `hilltop-heights-14-ram-nagar-nagpur`; the project was later renamed
  // to `hilltop-heights-14`. Anyone who bookmarked the old URL lands here.
  if (params.slug === "hilltop-heights-14-ram-nagar-nagpur") {
    return {
      redirect: {
        destination: "/projects/hilltop-heights-14",
        permanent: true,
      },
      revalidate: 3600,
    };
  }
  const { getProjectBySlug, getAllProjects } = await import("@/lib/projects");
  const product = await getProjectBySlug(params.slug);
  if (!product) return { notFound: true, revalidate: 60 };

  const relatedProjects = await getAllProjects();
  const latestBlogs = await getLatestBlogs(4);

  return {
    props: { product, latestBlogs, relatedProjects },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  if (!isProjectsEnabled()) return { paths: [], fallback: false };
  const { getAllProjects } = await import("@/lib/projects");
  const projects = await getAllProjects();
  return {
    paths: projects
      // Redirect-only aliases must be resolved through the blocking fallback.
      // Next.js 16 rejects redirects returned while prerendering paths that
      // were explicitly supplied by getStaticPaths.
      .filter(
        (p) =>
          (p.slug || productSlug(p.title)) !==
          "hilltop-heights-14-ram-nagar-nagpur"
      )
      .map((p) => ({
        params: { slug: p.slug || productSlug(p.title) },
      })),
    // Legacy alias handled by the redirect in getStaticProps above. We
    // still allow blocking fallback so any slugs added to the CMS between
    // builds render on first hit instead of 404'ing until the next build.
    fallback: "blocking",
  };
}
